import config from '@/utils/ApiUtils'
import { fetchWithToken } from '../utils/Keycloak'

const ExternalConnectionService = {
  
  // ===== IMPORT - INSCRIPTIONS =====
  
  /**
   * Créer une inscription import
   * @param {object} registrationData - Données de l'inscription
   * @returns {Promise} Inscription créée
   */
  createImportRegistration(registrationData) {
    return fetchWithToken(`${config().externalConnectionBackend}import/registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister toutes les inscriptions import
   * @returns {Promise} Liste des inscriptions import
   */
  getAllImportRegistrations() {
    return fetchWithToken(`${config().externalConnectionBackend}import/registration`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les inscriptions import par client
   * @param {number} clientid - ID du client
   * @returns {Promise} Inscriptions du client
   */
  getImportRegistrationsByClient(clientid) {
    return fetchWithToken(`${config().externalConnectionBackend}import/registration/client/${clientid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Modifier une inscription import
   * @param {string} idRegister - UUID de l'inscription
   * @param {object} registrationData - Nouvelles données
   * @returns {Promise} Inscription modifiée
   */
  updateImportRegistration(idRegister, registrationData) {
    return fetchWithToken(`${config().externalConnectionBackend}import/registration/${idRegister}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  // ===== IMPORT - MÉTÉO =====
  // Le service météo (erp-integration-insert-meteo) a sa propre table
  // registrationMeteo : ses inscriptions ne passent pas par integration-management.

  /**
   * Créer une inscription météo
   * @param {object} registrationData - { clientid, latitude, longitude, historyYears, forecastDays }
   * @returns {Promise} Inscription créée
   */
  createMeteoRegistration(registrationData) {
    return fetchWithToken(`${config().meteoBackend}registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister toutes les inscriptions météo
   * @returns {Promise} Liste des inscriptions météo
   */
  getAllMeteoRegistrations() {
    return fetchWithToken(`${config().meteoBackend}registration`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les inscriptions météo d'un client
   * @param {number} clientid - ID du client
   * @returns {Promise} Inscriptions météo du client
   */
  getMeteoRegistrationsByClient(clientid) {
    return fetchWithToken(`${config().meteoBackend}registration/client/${clientid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Modifier une inscription météo
   * @param {string} idRegister - UUID de l'inscription
   * @param {object} registrationData - Nouvelles données
   * @returns {Promise} Inscription modifiée
   */
  updateMeteoRegistration(idRegister, registrationData) {
    return fetchWithToken(`${config().meteoBackend}registration/${idRegister}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Supprimer une inscription météo
   * @param {string} idRegister - UUID de l'inscription
   * @returns {Promise} Confirmation de suppression
   */
  deleteMeteoRegistration(idRegister) {
    return fetchWithToken(`${config().meteoBackend}registration/${idRegister}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Consulter la météo enregistrée pour un client
   * @param {number} clientid - ID du client
   * @param {object} params - Paramètres optionnels { startDate, endDate }
   * @returns {Promise} Journées météo, de la plus ancienne à la plus récente
   */
  getMeteoByClient(clientid, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return fetchWithToken(`${config().meteoBackend}meteo/client/${clientid}${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  // ===== IMPORT - TYPES =====

  /**
   * Créer un type import
   * @param {object} typeData - Données du type
   * @returns {Promise} Type créé
   */
  createImportType(typeData) {
    const payload = (typeData.path === '' || typeData.path == null) && (typeData.taxe === 0 || typeData.taxe === '0')
      ? { name: typeData.name }
      : typeData;
    return fetchWithToken(`${config().externalConnectionBackend}import/types`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister tous les types import
   * @returns {Promise} Liste des types import
   */
  getAllImportTypes() {
    return fetchWithToken(`${config().externalConnectionBackend}import/types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les types import par client
   * @param {number} clientid - ID du client
   * @returns {Promise} Types du client
   */
  getImportTypesByClient(clientid) {
    return fetchWithToken(`${config().externalConnectionBackend}import/types/client/${clientid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Modifier un type import
   * @param {string} idType - UUID du type
   * @param {object} typeData - Nouvelles données
   * @returns {Promise} Type modifié
   */
  updateImportType(idType, typeData) {
    // Toujours envoyer le champ name (et autres champs modifiés)
    const payload = {
      name: typeData.name,
      path: typeData.path,
      taxe: typeData.taxe
    };
    return fetchWithToken(`${config().externalConnectionBackend}import/types/${idType}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  // ===== IMPORT - HISTORIQUE =====

  /**
   * Créer un log import
   * @param {object} historyData - Données du log
   * @returns {Promise} Log créé
   */
  createImportHistory(historyData) {
    return fetchWithToken(`${config().externalConnectionBackend}import/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(historyData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique import avec filtres
   * @param {object} filters - Filtres (clientid, serviceName, onError, limit, offset)
   * @returns {Promise} Historique filtré
   */
  getImportHistory(filters = {}) {
    const queryParams = new URLSearchParams();
    
    if (filters.clientid) queryParams.append('clientid', filters.clientid);
    if (filters.serviceName) queryParams.append('serviceName', filters.serviceName);
    if (filters.onError !== undefined) queryParams.append('onError', filters.onError);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const url = `${config().externalConnectionBackend}import/history${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique import par client
   * @param {number} clientid - ID du client
   * @returns {Promise} Historique du client
   */
  getImportHistoryByClient(clientid) {
    return fetchWithToken(`${config().externalConnectionBackend}import/history/client/${clientid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique import avec informations de type enrichies
   * @param {object} filters - Filtres (clientid, serviceName, onError, limit, offset)
   * @returns {Promise} Historique enrichi avec typeInfo
   */
  getImportHistoryWithTypes(filters = {}) {
    const queryParams = new URLSearchParams();
    
    if (filters.clientid) queryParams.append('clientid', filters.clientid);
    if (filters.serviceName) queryParams.append('serviceName', filters.serviceName);
    if (filters.onError !== undefined) queryParams.append('onError', filters.onError);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const url = `${config().externalConnectionBackend}import/history/with-types${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique import avec types pour un client spécifique
   * @param {number} clientid - ID du client
   * @returns {Promise} Historique enrichi du client
   */
  getImportHistoryWithTypesByClient(clientid) {
    return fetchWithToken(`${config().externalConnectionBackend}import/history/with-types/client/${clientid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  // ===== EXPORT - HISTORIQUE =====

  /**
   * Créer un historique export
   * @param {object} historyData - Données du log
   * @returns {Promise} Log créé
   */
  createExportHistory(historyData) {
    return fetchWithToken(`${config().externalConnectionBackend}export/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(historyData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique export avec filtres
   * @param {object} filters - Filtres (clientid, serviceName, onError, limit, offset)
   * @returns {Promise} Historique filtré
   */
  getExportHistory(filters = {}) {
    const queryParams = new URLSearchParams();
    
    if (filters.clientid) queryParams.append('clientid', filters.clientid);
    if (filters.serviceName) queryParams.append('serviceName', filters.serviceName);
    if (filters.onError !== undefined) queryParams.append('onError', filters.onError);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const url = `${config().externalConnectionBackend}export/history${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique export d'un client, paginé par envoi
   * @param {number} clientid - ID du client
   * @param {object} filters - limit et offset (en envois), dateStart et dateEnd (YYYY-MM-DD, incluses)
   * @returns {Promise} { data: envois, total: nombre d'envois correspondant aux filtres }
   */
  getExportHistoryByClient(clientid, filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);
    if (filters.dateStart) queryParams.append('dateStart', filters.dateStart);
    if (filters.dateEnd) queryParams.append('dateEnd', filters.dateEnd);

    const url = `${config().externalConnectionBackend}export/history/client/${clientid}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Télécharger le fichier déposé chez l'ERP pour un envoi (copie S3, exports VBO)
   * @param {number} exportId - ID de l'envoi
   * @returns {Promise} Blob du fichier
   */
  downloadExportFile(exportId) {
    return fetchWithToken(`${config().externalConnectionBackend}export/history/${exportId}/file`, {
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then(body => {
              throw new Error(body.error || `Erreur ${response.status}`);
            });
        }
        return response.blob();
      });
  },

  // ===== EXPORT - INSCRIPTIONS =====

  /**
   * Créer une inscription export
   * @param {object} registrationData - Données de l'inscription
   * @returns {Promise} Inscription créée
   */
  createExportRegistration(registrationData) {
    return fetchWithToken(`${config().externalConnectionBackend}export/registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister toutes les inscriptions export
   * @returns {Promise} Liste des inscriptions export
   */
  getAllExportRegistrations() {
    return fetchWithToken(`${config().externalConnectionBackend}export/registration`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister tous les types export
   * @returns {Promise} Liste des types export
   */
  getAllExportTypes() {
    return fetchWithToken(`${config().externalConnectionBackend}export/types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister tous les modèles d'export
   * @returns {Promise} Liste des modèles d'export
   */
  getAllExportModels() {
    // NOTE: backend confirmé: integration-management/export/models
    return fetchWithToken(`${config().externalConnectionBackend}export/models`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Obtenir un modèle d'export par ID
   * @param {string} idModelExport - ID du modèle d'export
   * @returns {Promise} Modèle d'export
   */
  getExportModelById(idModelExport) {
    return fetchWithToken(`${config().externalConnectionBackend}export/models/${idModelExport}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Créer un modèle d'export
   * @param {Object} exportModel - Données du modèle d'export
   * @param {string} exportModel.idModelExport - ID du modèle
   * @param {string} exportModel.nom - Nom du modèle
   * @returns {Promise} Modèle d'export créé
   */
  createExportModel(exportModel) {
    return fetchWithToken(`${config().externalConnectionBackend}export/models`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(exportModel)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Mettre à jour un modèle d'export
   * @param {string} idModelExport - ID du modèle d'export
   * @param {Object} updates - Données à mettre à jour
   * @param {string} updates.nom - Nom du modèle
   * @returns {Promise} Modèle d'export mis à jour
   */
  updateExportModel(idModelExport, updates) {
    return fetchWithToken(`${config().externalConnectionBackend}export/models/${idModelExport}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Supprimer un modèle d'export
   * @param {string} idModelExport - ID du modèle d'export
   * @returns {Promise} Réponse de suppression
   */
  deleteExportModel(idModelExport) {
    return fetchWithToken(`${config().externalConnectionBackend}export/models/${idModelExport}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Créer un type export
   * @param {Object} exportType - Données du type export
   * @returns {Promise} Type export créé
   */
  createExportType(exportType) {
    return fetchWithToken(`${config().externalConnectionBackend}export/types`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(exportType)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Mettre à jour un type export
   * @param {number} exportTypeId - ID du type export
   * @param {Object} exportType - Données du type export
   * @returns {Promise} Type export mis à jour
   */
  updateExportType(exportTypeId, exportType) {
    return fetchWithToken(`${config().externalConnectionBackend}export/types/${exportTypeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(exportType)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Supprimer un type export
   * @param {number} exportTypeId - ID du type export
   * @returns {Promise} Réponse de suppression
   */
  deleteExportType(exportTypeId) {
    return fetchWithToken(`${config().externalConnectionBackend}export/types/${exportTypeId}`, {
      method: 'DELETE'
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister les types export par client
   * @param {number} clientId - ID du client
   * @returns {Promise} Liste des types export du client
   */
  getExportTypesByClient(clientId) {
    return fetchWithToken(`${config().externalConnectionBackend}export/types/client/${clientId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lister l'historique des inscriptions export
   * @returns {Promise} Historique des inscriptions export
   */
  getExportRegistrationHistory() {
    return fetchWithToken(`${config().externalConnectionBackend}export/registration/history`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les inscriptions export par client
   * @param {number} clientid - ID du client
   * @returns {Promise} Inscriptions du client
   */
  getExportRegistrationsByClient(clientid) {
    return fetchWithToken(`${config().externalConnectionBackend}export/registration/client/${clientid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Modifier une inscription export
   * @param {string} idRegistration - UUID de l'inscription
   * @param {object} registrationData - Nouvelles données
   * @returns {Promise} Inscription modifiée
   */
  updateExportRegistration(idRegistration, registrationData) {
    return fetchWithToken(`${config().externalConnectionBackend}export/registration/${idRegistration}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  // ===== UTILITAIRES =====

  /**
   * Vérifie le statut de l'API integration-management
   * @returns {Promise} Statut de l'API
   */
  getApiStatus() {
    return fetchWithToken(`${config().externalConnectionBackend}status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les détails d'un historique d'import
   * @param {number} historyId - ID de l'historique
   * @returns {Promise} Détails de l'historique
   */
  getImportHistoryDetails(historyId) {
    return fetchWithToken(`${config().externalConnectionBackend}import/history/${historyId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  // ===== MÉTHODES DE MODIFICATION/SUPPRESSION =====

  /**
   * Supprimer une inscription import
   * @param {string} idRegister - ID de l'inscription à supprimer
   * @returns {Promise} Confirmation de suppression
   */
  deleteImportRegistration(idRegister) {
    return fetchWithToken(`${config().externalConnectionBackend}import/registration/${idRegister}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Supprimer une inscription export
   * @param {string} idRegistration - ID de l'inscription à supprimer
   * @returns {Promise} Confirmation de suppression
   */
  deleteExportRegistration(idRegistration) {
    return fetchWithToken(`${config().externalConnectionBackend}export/registration/${idRegistration}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Supprimer un type import
   * @param {string} idType - ID du type à supprimer
   * @returns {Promise} Confirmation de suppression
   */
  deleteImportType(idType) {
    return fetchWithToken(`${config().externalConnectionBackend}import/types/${idType}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },


  // ===== VUSION - API SPÉCIALISÉE =====

  /**
   * Récupérer le statut de la dernière synchronisation VUSION
   * @returns {Promise} Statut de synchronisation
   */
  getVusionSyncStatus() {
    return fetchWithToken(`${config().vusionBackend}events`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (response.status === 404) {
          throw new Error('Item non trouvé');
        }
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer le statut d'une synchronisation VUSION par correlation ID
   * @param {string} correlationId - ID de corrélation
   * @returns {Promise} Statut de synchronisation
   */
  getVusionSyncStatusByCorrelationId(correlationId) {
    return fetchWithToken(`${config().vusionBackend}events/${correlationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (response.status === 404) {
          throw new Error('Item non trouvé');
        }
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les labels ESL liés à un produit VUSION
   * @param {string} ean - Code-barres du produit
   * @returns {Promise} Labels ESL du produit
   */
  getVusionProductLabels(ean) {
    return fetchWithToken(`${config().vusionBackend}products/${ean}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (response.status === 204) {
          return { labels: [], ean, message: 'Aucun ESL lié' };
        }
        if (response.status === 404) {
          throw new Error('Produit non trouvé');
        }
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Mettre à jour un produit VUSION avec données d'étiquette complètes
   * @param {string} ean - Code-barres du produit
   * @param {object} productData - Données complètes du produit
   * @returns {Promise} Résultat de la mise à jour
   */
  updateVusionProduct(ean, productData) {
    return fetchWithToken(`${config().vusionBackend}product/${ean}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(productData)
    })
      .then(response => {
        if (response.status === 404) {
          throw new Error('Produit non trouvé');
        }
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Lier ou délier un label ESL à un produit VUSION
   * @param {string} ean - Code-barres du produit
   * @param {string} labelId - ID du label ESL
   * @param {boolean} unlink - Délier le label (optionnel)
   * @returns {Promise} Résultat de l'opération
   */
  manageVusionLabel(ean, labelId, unlink = false) {
    const body = { ean, labelId };
    if (unlink) {
      body.unlink = true;
    }

    return fetchWithToken(`${config().vusionBackend}label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(response => {
        if (response.status === 404) {
          throw new Error('Produit non trouvé');
        }
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Obtenir les valeurs par défaut pour un produit VUSION
   * @returns {object} Données par défaut pour un produit VUSION
   */
  getVusionDefaultProductData() {
    return {
      allergenes: [{ idAllergene: 3, nom: "Sulfites" }],
      capture: [{ nom: "Pêché(es)", idCapture: 1 }],
      conditionnement: [{ nom: "Kg", idConditionnementVente: 2 }],
      eau: [{ nom: "Mer", idEau: 2 }],
      engins: [{ idEngin: 1, enginOrder: 0, color: "#0076C7", nom: "Chalut" }],
      prix: 0,
      idEtiquetteStyle: 1,
      denomination: "Cabillaud",
      latin: [{ nom: "Conger conger", idLatinPoisson: 341, idPoisson: 49 }],
      sousZone: [{ idSousZone: 10, idSouszone: 10, nom: "Manche et Mers Celtiques" }],
      zone: [{ idZone: 2, nom: "Atlantique Nord-Est" }],
      preparation: [{ idPreparation: 1, nom: "saumuré(es)" }],
      congelation: [{ nom: "Décongelé", idCongelation: 2 }],
      compositionEau: "true"
    };
  },

  /**
   * Créer une inscription VUSION avec key et store
   * @param {object} registrationData - Données de l'inscription VUSION (key, store, clientId, etc.)
   * @returns {Promise} Inscription créée
   */
  createVusionRegistration(registrationData) {
    return fetchWithToken(`${config().vusionBackend}subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Mettre à jour une inscription VUSION
   * @param {string} id - UUID de la subscription
   * @param {object} registrationData - Nouvelles données VUSION (clientId, key, store)
   * @returns {Promise} Inscription mise à jour
   */
  updateVusionRegistration(id, registrationData) {
    return fetchWithToken(`${config().vusionBackend}subscription/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationData)
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer les inscriptions VUSION par client
   * @param {number} clientId - ID du client
   * @returns {Promise} Inscriptions VUSION du client
   */
  getVusionRegistrationsByClient(clientId) {
    return fetchWithToken(`${config().vusionBackend}subscription?clientId=${clientId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (response.status === 404) {
          // Aucune subscription trouvée, retourner un tableau vide
          return [];
        }
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json().then(data => {
          // Si c'est un objet unique, le transformer en tableau
          return Array.isArray(data) ? data : [data];
        });
      });
  },

  /**
   * Récupérer l'historique des liens VUSION
   * @param {number} clientId - ID du client
   * @param {object} params - Paramètres optionnels { limit, offset }
   * @returns {Promise} Historique des liens d'étiquettes
   */
  getVusionLinkHistory(clientId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const queryString = queryParams.toString();
    const url = `${config().vusionBackend}history/links/${clientId}${queryString ? '?' + queryString : ''}`;
    
    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique des étiquettes VUSION
   * @param {number} clientId - ID du client
   * @param {object} params - Paramètres optionnels { limit, offset }
   * @returns {Promise} Historique des envois d'étiquettes
   */
  getVusionLabelHistory(clientId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const queryString = queryParams.toString();
    const url = `${config().vusionBackend}history/etiquettes/${clientId}${queryString ? '?' + queryString : ''}`;
    
    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique complet VUSION (liens + étiquettes)
   * @param {number} clientId - ID du client
   * @param {object} params - Paramètres optionnels { limit, offset }
   * @returns {Promise} Historique complet avec links et etiquettes
   */
  getVusionHistoryComplete(clientId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const queryString = queryParams.toString();
    const url = `${config().vusionBackend}history/${clientId}${queryString ? '?' + queryString : ''}`;
    
    return fetchWithToken(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique des produits VUSION via /product
   * @param {number} clientId - ID du client
   * @returns {Promise} Historique des produits
   */
  getVusionProductHistory(clientId) {
    return fetchWithToken(`${config().vusionBackend}product?clientId=${clientId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer l'historique des labels VUSION via /label
   * @param {number} clientId - ID du client
   * @returns {Promise} Historique des labels ESL
   */
  getVusionLabelHistoryDirect(clientId) {
    return fetchWithToken(`${config().vusionBackend}label?clientId=${clientId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Supprimer une inscription VUSION
   * @param {string} id - UUID de la subscription à supprimer
   * @returns {Promise} Confirmation de suppression
   */
  deleteVusionRegistration(id) {
    return fetchWithToken(`${config().vusionBackend}subscription/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  },

  /**
   * Récupérer toutes les subscriptions VUSION (admin)
   * @returns {Promise} Liste de toutes les subscriptions avec count
   */
  getAllVusionSubscriptions() {
    return fetchWithToken(`${config().vusionBackend}subscriptions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(errorText => {
            throw new Error(`Erreur ${response.status}: ${errorText}`);
          });
        }
        return response.json();
      });
  }
}

export default ExternalConnectionService